import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  SheetHeader,
  SheetTitle,
  usePreventSheetClose,
} from "@/components/ui/sheet";
import {
  Department,
  useAdminUpsertDepartmentMutation,
} from "@common/utils/graphql";
import { UseFormReturn } from "react-hook-form";
import { z } from "zod";
import { v4 as uuidv4 } from "uuid";
import { DepartmentFormSchema } from "../forms/Forms";
import { useToast } from "@/components/ui/use-toast";
import { SheetBackButton } from "@common/components/SheetActions/SheetBackButton";
import { hasDirtyFields } from "@/utils/utils";

interface Props {
  onBack: () => void;
  form: UseFormReturn<z.infer<typeof DepartmentFormSchema>>;
  mode: "create" | "edit";
  department?: Department;
}

export const CreateOrEditDepartmentContent = ({
  form,
  onBack,
  mode,
  department,
}: Props) => {
  const isEditing = mode === "edit";
  const { toast } = useToast();
  const [upsertDepartment, { loading }] = useAdminUpsertDepartmentMutation();
  usePreventSheetClose(() => hasDirtyFields(form) && isEditing);

  const createDepartment = async () => {
    try {
      const output = await form.trigger(undefined, { shouldFocus: true });
      if (!output) {
        return;
      }

      const values = form.getValues();

      let uuid = uuidv4();
      if (isEditing) {
        uuid = department!.uuid;
      }
      const { data } = await upsertDepartment({
        variables: {
          isShown: true,
          name: values.name,
          uuid,
        },
      });
      if (
        data?.adminUpsertDepartment.__typename !==
        "MutationAdminUpsertDepartmentSuccess"
      ) {
        throw new Error();
      }
      onBack();
    } catch (e) {
      toast({
        title: `Failed to ${isEditing ? "edit" : "create"} department, please try again`,
        variant: "destructive",
      });
    }
  };

  const title = isEditing ? "Edit Department" : "Create Department";
  const buttonText = isEditing ? "Save Changes" : "Create Department";
  const loadingButtonText = isEditing
    ? "Saving Changes"
    : "Creating Department";
  return (
    <>
      {isEditing && <SheetBackButton onClick={onBack} />}
      <SheetHeader>
        <SheetTitle>{title}</SheetTitle>
      </SheetHeader>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(() => {})}
          className="flex flex-1 flex-col space-y-4 px-sheet-gap pb-sheet-gap"
        >
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Department Name</FormLabel>
                <FormControl>
                  <Input placeholder="Name" disabled={loading} {...field} />
                </FormControl>
              </FormItem>
            )}
          />
          <div className="flex-1" />
          <Button onClick={createDepartment} disabled={loading}>
            {loading ? loadingButtonText : buttonText}
          </Button>
        </form>
      </Form>
    </>
  );
};
